import { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import colors from '../../styles/colors';
import { apiGetDailyClaimStatus, apiClaimDailyCredits } from '../../api/userCardApi';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { updateCardAccount } from '../../store/slices/authSlice';

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(76,175,111,0.4); }
  50%       { box-shadow: 0 0 0 6px rgba(76,175,111,0); }
`;

const Banner = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background-color: ${colors.bgColorBlack2};
  border: 1px solid ${({ $active }) => ($active ? 'rgba(76,175,111,0.5)' : 'rgba(255,255,255,0.08)')};
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 32px;
  width: 100%;
  max-width: 480px;
  transition: border-color 0.3s;
`;

const BannerLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const BannerLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: rgba(255,255,255,0.35);
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const BannerValue = styled.span<{ $active: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ $active }) => ($active ? '#4caf6f' : 'rgba(255,255,255,0.5)')};
`;

const ClaimBtn = styled.button<{ $active: boolean }>`
  padding: 9px 20px;
  border-radius: 7px;
  border: 1px solid ${({ $active }) => ($active ? '#4caf6f' : 'rgba(255,255,255,0.1)')};
  background-color: ${({ $active }) => ($active ? 'rgba(76,175,111,0.18)' : 'transparent')};
  color: ${({ $active }) => ($active ? '#4caf6f' : 'rgba(255,255,255,0.25)')};
  font-size: 13px;
  font-weight: 700;
  cursor: ${({ $active }) => ($active ? 'pointer' : 'default')};
  white-space: nowrap;
  flex-shrink: 0;
  animation: ${({ $active }) => ($active ? pulse : 'none')} 2s infinite;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${({ $active }) => ($active ? 'rgba(76,175,111,0.28)' : 'transparent')};
  }
`;

const FeedbackText = styled.span<{ $type: 'success' | 'error' }>`
  font-size: 11px;
  color: ${({ $type }) => ($type === 'success' ? '#4caf6f' : '#e05555')};
  margin-top: 2px;
`;

const getCountdown = (nextClaimAt: string): string => {
  const diff = new Date(nextClaimAt).getTime() - Date.now();
  if (diff <= 0) return '';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

interface Props {
  onClaimed: (creditsRemaining: number) => void;
}

const DailyClaimButton: React.FC<Props> = ({ onClaimed }) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user ?? state.auth.userLoggedState);
  const [canClaim, setCanClaim] = useState(false);
  const [nextClaimAt, setNextClaimAt] = useState<string | null>(null);
  const [creditsToGive, setCreditsToGive] = useState<number | null>(null);
  const [countdown, setCountdown] = useState('');
  const [claiming, setClaiming] = useState(false);
  const [feedback, setFeedback] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const loadStatus = useCallback(() => {
    apiGetDailyClaimStatus()
      .then((data) => {
        setCanClaim(data.can_claim);
        setNextClaimAt(data.next_claim_at);
        setCreditsToGive(data.credits_to_claim);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    if (!nextClaimAt) return;
    const tick = () => {
      const c = getCountdown(nextClaimAt);
      if (!c) {
        setCanClaim(true);
        setNextClaimAt(null);
        setCountdown('');
      } else {
        setCountdown(c);
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [nextClaimAt]);

  const handleClaim = async () => {
    if (!canClaim || claiming) return;
    setClaiming(true);
    setFeedback(null);
    try {
      const result = await apiClaimDailyCredits();
      setCanClaim(false);
      setNextClaimAt(result.next_claim_at);
      onClaimed(result.credits_remaining);
      setFeedback({ msg: `+${result.credits_given} crediti riscattati!`, type: 'success' });
      // Aggiorna lo store Redux così il CardPodium in homepage riflette il cooldown
      if (user?.card_account) {
        dispatch(updateCardAccount({
          ...user.card_account,
          credits: result.credits_remaining,
          can_claim: false,
          next_claim_at: result.next_claim_at,
        }));
      }
    } catch (err: any) {
      if (err?.error === 'cooldown_active' && err?.next_claim_at) {
        setCanClaim(false);
        setNextClaimAt(err.next_claim_at);
      } else {
        loadStatus();
      }
      setFeedback({ msg: 'Non è ancora possibile riscattare', type: 'error' });
    } finally {
      setClaiming(false);
    }
  };

  const btnLabel = claiming
    ? 'Riscatto...'
    : canClaim
      ? 'Riscatta ora'
      : countdown
        ? `Torna tra ${countdown}`
        : '—';

  return (
    <Banner $active={canClaim}>
      <BannerLeft>
        <BannerLabel>Bonus giornaliero</BannerLabel>
        <BannerValue $active={canClaim}>
          {canClaim
            ? `${creditsToGive ?? '...'} crediti disponibili`
            : countdown ? 'Già riscattato oggi' : '—'}
        </BannerValue>
        {feedback && <FeedbackText $type={feedback.type}>{feedback.msg}</FeedbackText>}
      </BannerLeft>
      <ClaimBtn $active={canClaim} disabled={!canClaim || claiming} onClick={handleClaim}>
        {btnLabel}
      </ClaimBtn>
    </Banner>
  );
};

export default DailyClaimButton;
